import React, { useEffect, useState, useRef } from 'react'
import { FaShoppingCart, FaSearch, FaTimes } from 'react-icons/fa';
import "./navbar.modern.css"
import { useNavigate } from "react-router-dom";

export default function ModernNavbar() {
  const inputRef = useRef()
  const navigate = useNavigate()
  const [user, setUser] = useState(null);
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("")
  const [isSearching, setIsSearching] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [showMegaMenu, setShowMegaMenu] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showSearchOverlay, setShowSearchOverlay] = useState(false);
  const [popularSearches, setPopularSearches] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [siteSettings, setSiteSettings] = useState({});
  const [mobileBrandsOpen, setMobileBrandsOpen] = useState(false);

  useEffect(() => {
    const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
    fetch(`${API}/site-settings`)
      .then(res => res.json())
      .then(data => setSiteSettings(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const updateCartCount = () => {
      try {
        const cart = JSON.parse(localStorage.getItem('cart') || '[]');
        const count = cart.reduce((acc, item) => acc + item.quantity, 0);
        setCartCount(count);
      } catch (error) {
        console.error("Error fetching cart count:", error);
        setCartCount(0);
      }
    };

    // Check for logged in user
    const checkUser = () => {
      try {
        const userData = localStorage.getItem('user');
        if (userData) {
          setUser(JSON.parse(userData));
        }
      } catch (error) {
        console.error("Error checking user:", error);
      }
    };

    updateCartCount();
    checkUser();

    const handleCartUpdate = () => {
      updateCartCount();
    };

    const handleCartIncrement = (event) => {
      setCartCount(prev => prev + (event.detail?.quantity || 1));
    };

    const handleUserUpdate = () => {
      checkUser();
    };

    // Close dropdown when clicking outside
    const handleClickOutside = (event) => {
      if (showUserDropdown && !event.target.closest('.user-menu-wrapper')) {
        setShowUserDropdown(false);
      }
    };

    window.addEventListener('cartUpdated', handleCartUpdate);
    window.addEventListener('cartCountIncrement', handleCartIncrement);
    window.addEventListener('userUpdated', handleUserUpdate);
    document.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('cartUpdated', handleCartUpdate);
      window.removeEventListener('cartCountIncrement', handleCartIncrement);
      window.removeEventListener('userUpdated', handleUserUpdate);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showUserDropdown]);

  // Load popular searches and trending products when overlay opens
  useEffect(() => {
    if (showSearchOverlay) {
      // Auto-focus search input
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);

      // Load popular categories
      const categories = ['Gel Polish', 'Lacquer Polish', 'Dipping Powder', 'LED Lamps', 'Acrylic', 'Cuticle Nipper'];
      setPopularSearches(categories);

      // Load trending products (real data from product groups)
      const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      fetch(`${API}/product-groups?limit=6`)
        .then(res => res.json())
        .then(data => {
          setTrendingProducts(data.data || []);
        })
        .catch(error => {
          console.error('Error loading trending products:', error);
        });
    }
  }, [showSearchOverlay]);

  const handleSignOut = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    setUser(null);
    setShowUserDropdown(false);
    navigate('/');
  };

  const toggleUserDropdown = () => {
    setShowUserDropdown(!showUserDropdown);
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);

    if (query.trim() === '') {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const API = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
      const res = await fetch(`${API}/product-groups?search=${encodeURIComponent(query)}&limit=8`);
      const data = await res.json();
      setSearchResults(data.data || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchResultClick = (product) => {
    navigate(`/product-group/${product.group_id}`);
    setShowSearchOverlay(false);
    setSearchQuery('');
    setSearchResults([]);
  };

  const handleSearchSubmit = () => {
    if (searchQuery.trim()) {
      navigate(`/products?search=${encodeURIComponent(searchQuery.trim())}`);
      setShowSearchOverlay(false);
      setSearchQuery('');
      setSearchResults([]);
    }
  };

  const handlePopularSearchClick = (category) => {
    navigate(`/products?type=${encodeURIComponent(category)}`);
    setShowSearchOverlay(false);
  };

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
  
  const getImageUrl = (localPath) => {
    if (!localPath) return null;
    if (localPath.startsWith('http')) return localPath;
    const baseUrl = API_URL.replace('/api', '');
    if (localPath.startsWith('/uploads/')) return `${baseUrl}${localPath}`;
    return `${baseUrl}/api/images/${localPath}`;
  };

  return (
    <div className="modern-navbar-wrapper">
      {/* Top Bar */}
      <div className="top-bar-modern">
        <div className="top-bar-content">
          {siteSettings.show_announcement_bar !== 'false' && (<>
          <div className="top-bar-left">
            <span>{siteSettings.announcement_left || 'Free shipping on orders over $75'}</span>
          </div>
          <div className="top-bar-right">
            {(siteSettings.announcement_right_1 || 'Same-day pickup available') && <span>{siteSettings.announcement_right_1 || 'Same-day pickup available'}</span>}
            {(siteSettings.announcement_right_2 || 'Join Rewards Program') && <span>{siteSettings.announcement_right_2 || 'Join Rewards Program'}</span>}
          </div>
          </>)}
        </div>
      </div>

      {/* Main Header */}
      <header className="main-header-modern">
        <div className="header-content-modern">
          {/* Mobile Menu Button */}
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span>☰</span>
          </button>

          {/* Logo */}
          <div className="logo-modern" onClick={() => navigate("/")}>
            <img src="/images/tc-nails-depot-logo.webp" alt="TC Nails Depot" />
          </div>

          {/* Center Navigation */}
          <nav className="center-nav-modern">
            <a href="/products" className="nav-link-modern">Shop All</a>
            <a href="/products?filter=new" className="nav-link-modern">New Arrivals</a>
            <a href="/best-sellers" className="nav-link-modern">Best Sellers</a>
            <a href="/products?filter=sale" className="nav-link-modern">Deals</a>
            
            <div 
              className="nav-link-modern nav-link-shop"
              onMouseEnter={() => setShowMegaMenu(true)}
              onMouseLeave={() => setShowMegaMenu(false)}
            >
              <span>Shop</span>
              <span className="nav-arrow-inline">▼</span>
              
              {showMegaMenu && (
                <div className="mega-menu-modern">
                  <div className="mega-menu-content">
                    <div className="mega-menu-column">
                      <h4>By Category</h4>
                      <a href="/products?type=Gel Polish">Gel Polish</a>
                      <a href="/products?type=Lacquer Polish">Lacquer Polish</a>
                      <a href="/products?type=Bundle Gel & Lacquer">Gel & Lacquer Bundles</a>
                      <a href="/products?type=Dip & Acrylic">Dip & Acrylic</a>
                      <a href="/products?type=Gel Art Liner">Nail Art</a>
                      <a href="/products?type=Spa & Wellness">Spa & Wellness</a>
                    </div>
                    
                    <div className="mega-menu-column">
                      <h4>Top Brands</h4>
                      <a href="/brands/opi">OPI</a>
                      <a href="/brands/dnd">DND</a>
                      <a href="/brands/pla">PLA</a>
                      <a href="/brands/niko">NIKO</a>
                      <a href="/brands/la-palm">LA PALM</a>
                      <a href="/brands/ibd">IBD</a>
                    </div>
                    
                    <div className="mega-menu-column">
                      <h4>More Brands</h4>
                      <a href="/brands/cre8tion">Cre8tion</a>
                      <a href="/brands/nghia">NGHIA</a>
                      <a href="/brands/nexgen">Nexgen</a>
                      <a href="/brands/kupa">Kupa</a>
                      <a href="/brands/led-lamps">LED Lamps</a>
                      <a href="/brands/vichy-spa">Vichy Spa</a>
                      <a href="/brands/md-creative">MD Creative</a>
                      <a href="/brands/sns">SNS</a>
                    </div>
                    
                    <div className="mega-menu-column">
                      <h4>Shop By</h4>
                      <a href="/shop?category=nail-art">Nail Supplies</a>
                      <a href="/shop?category=led-uv-lamps">LED Lamps</a>
                      <a href="/shop?category=base-top-coats">Base & Top Coats</a>
                      <a href="/shop?category=builder-gel">Builder Gel</a>
                      <a href="/products">View All Products</a>
                    </div>
                    
                    <div className="mega-menu-featured">
                      <img src="/images/OPI.png" alt="Featured" />
                      <div className="mega-menu-featured-text">
                        <h4>2,500+ Products</h4>
                        <p>Explore our full collection</p>
                        <button className="mega-menu-btn" onClick={() => navigate('/products')}>Shop Now</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </nav>

          {/* Header Actions */}
          <div className="header-actions-modern">
            <button className="header-action-btn" onClick={() => setShowSearchOverlay(true)}>
              <FaSearch size={18} />
            </button>
            
            <button className="header-action-btn cart-btn" onClick={() => navigate("/cart")}>
              <FaShoppingCart size={20} />
              {cartCount > 0 && <span className="cart-badge-modern">{cartCount}</span>}
            </button>

            {user ? (
              <div className="user-menu-wrapper">
                <button 
                  className="header-action-btn signin-btn user-menu-btn"
                  onClick={toggleUserDropdown}
                >
                  <span className="action-label">{user.firstName}</span>
                </button>
                {showUserDropdown && (
                  <div className="user-dropdown-menu">
                    {(user.role === 'admin' || user.role === 'staff') ? (
                      <button onClick={() => { navigate('/management'); setShowUserDropdown(false); }} className="dropdown-item">
                        Management
                      </button>
                    ) : (
                      <button onClick={() => { navigate('/orders'); setShowUserDropdown(false); }} className="dropdown-item">
                        My Orders
                      </button>
                    )}
                    <button onClick={handleSignOut} className="dropdown-item">
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button className="header-action-btn signin-btn" onClick={() => navigate("/login")}>
                <span className="action-label">Sign In</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Search Overlay */}
      {showSearchOverlay && (
        <div className="search-overlay-modern">
          <div className="search-overlay-content">
            <button className="search-overlay-close" onClick={() => setShowSearchOverlay(false)}>
              <FaTimes size={24} />
            </button>

            <div className="search-overlay-input-wrapper">
              <FaSearch className="search-overlay-icon" size={24} />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search for products, brands, or categories..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSearchSubmit(); }}
                className="search-overlay-input"
              />
            </div>

            {searchQuery ? (
              <div className="search-overlay-results">
                {isSearching ? (
                  <div className="search-overlay-loading">Searching...</div>
                ) : searchResults.length === 0 ? (
                  <div className="search-overlay-empty">No results found for "{searchQuery}"</div>
                ) : (
                  <div className="search-results-grid">
                    {searchResults.map((product) => (
                      <div
                        key={product.group_id}
                        className="search-result-card"
                        onClick={() => handleSearchResultClick(product)}
                      >
                        <div className="search-result-image">
                          {(product.hero_image || product.preview_variants?.[0]?.image_url) ? (
                            <img src={getImageUrl(product.hero_image || product.preview_variants[0].image_url)} alt={product.title} />
                          ) : (
                            <div className="no-image">No Image</div>
                          )}
                        </div>
                        <div className="search-result-details">
                          <p className="search-result-brand">{product.vendor_name || product.brand_name}</p>
                          <h4 className="search-result-name">{product.title}</h4>
                          <p className="search-result-price">
                            ${Number(product.min_price).toFixed(2)}
                            {product.max_price && product.max_price !== product.min_price && (
                              <span> - ${Number(product.max_price).toFixed(2)}</span>
                            )}
                          </p>
                        </div>
                      </div>
                    ))}
                    {searchResults.length > 0 && (
                      <button className="search-view-all-btn" onClick={handleSearchSubmit}>
                        View all results for "{searchQuery}"
                      </button>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div className="search-overlay-suggestions">
                {/* Popular Searches */}
                <div className="search-suggestion-section">
                  <h3 className="search-suggestion-title">Popular Searches</h3>
                  <div className="popular-searches-grid">
                    {popularSearches.map((search, index) => (
                      <button
                        key={index}
                        className="popular-search-btn"
                        onClick={() => handlePopularSearchClick(search)}
                      >
                        {search}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Trending Products */}
                {trendingProducts.length > 0 && (
                  <div className="search-suggestion-section">
                    <h3 className="search-suggestion-title">Trending Products</h3>
                    <div className="trending-products-grid">
                      {trendingProducts.map((product) => (
                        <div
                          key={product.group_id}
                          className="trending-product-card"
                          onClick={() => handleSearchResultClick(product)}
                        >
                          <div className="trending-product-image">
                            {(product.hero_image || product.preview_variants?.[0]?.image_url) ? (
                              <img src={getImageUrl(product.hero_image || product.preview_variants[0].image_url)} alt={product.title} />
                            ) : (
                              <div className="no-image">No Image</div>
                            )}
                          </div>
                          <div className="trending-product-info">
                            <p className="trending-product-name">{product.title}</p>
                            <p className="trending-product-price">${Number(product.min_price).toFixed(2)}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="mobile-menu-modern">
          <div className="mobile-menu-header">
            <h3>Menu</h3>
            <button onClick={() => setMobileMenuOpen(false)}>
              <span>✕</span>
            </button>
          </div>
          <div className="mobile-menu-content">
            <a href="/products" onClick={() => setMobileMenuOpen(false)}>Shop All</a>
            <div className="mobile-brands-section">
              <button className="mobile-brands-toggle" onClick={() => setMobileBrandsOpen(!mobileBrandsOpen)}>
                Brands <span>{mobileBrandsOpen ? '▲' : '▼'}</span>
              </button>
              {mobileBrandsOpen && (
                <div className="mobile-brands-list">
                  <a href="/brands/opi" onClick={() => setMobileMenuOpen(false)}>OPI</a>
                  <a href="/brands/dnd" onClick={() => setMobileMenuOpen(false)}>DND</a>
                  <a href="/brands/pla" onClick={() => setMobileMenuOpen(false)}>PLA</a>
                  <a href="/brands/niko" onClick={() => setMobileMenuOpen(false)}>NIKO</a>
                  <a href="/brands/la-palm" onClick={() => setMobileMenuOpen(false)}>LA PALM</a>
                  <a href="/brands/cre8tion" onClick={() => setMobileMenuOpen(false)}>Cre8tion</a>
                  <a href="/brands/ibd" onClick={() => setMobileMenuOpen(false)}>IBD</a>
                  <a href="/brands/nghia" onClick={() => setMobileMenuOpen(false)}>NGHIA</a>
                  <a href="/brands/nexgen" onClick={() => setMobileMenuOpen(false)}>Nexgen</a>
                  <a href="/brands/kupa" onClick={() => setMobileMenuOpen(false)}>Kupa</a>
                  <a href="/brands/led-lamps" onClick={() => setMobileMenuOpen(false)}>LED Lamps</a>
                  <a href="/brands/vichy-spa" onClick={() => setMobileMenuOpen(false)}>Vichy Spa</a>
                  <a href="/brands/md-creative" onClick={() => setMobileMenuOpen(false)}>MD Creative</a>
                  <a href="/brands/sns" onClick={() => setMobileMenuOpen(false)}>SNS</a>
                </div>
              )}
            </div>
            <a href="/products?filter=new" onClick={() => setMobileMenuOpen(false)}>New Arrivals</a>
            <a href="/best-sellers" onClick={() => setMobileMenuOpen(false)}>Best Sellers</a>
            <a href="/products?filter=sale" onClick={() => setMobileMenuOpen(false)}>Deals</a>
            <a href="/about" onClick={() => setMobileMenuOpen(false)}>About Us</a>
            <a href="/orders" onClick={() => setMobileMenuOpen(false)}>My Orders</a>
          </div>
        </div>
      )}
    </div>
  )
}
